import React, { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import SoftButton from "components/SoftButton";
import { ApiResponse } from "model/apiResponseModel";
import api from "api/api";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import SoftBadge from "components/SoftBadge";
import { Link } from "react-router-dom";
import SearchField from "components/SearchField";
import { Box } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ConfirmDialog from "layouts/leaderboard/components/ConfirmDialog";
import DeleteTableButton from "components/DeleteTableButton";

const getCategoryConfig = (category) => {
  switch (category) {
    case "consistency":
      return { label: "Consistency", color: "info" };
    case "time_investment":
      return { label: "Time Investment", color: "primary" };
    case "completion":
      return { label: "Completion", color: "success" };
    case "effort":
      return { label: "Effort", color: "warning" };
    default:
      return { label: category, color: "secondary" };
  }
};

export default function Badges() {
  const location = useLocation();
  const [columns, setColumns] = useState([]);
  /** Grouped badge tables: { category, rows }[] when API returns groupByCategory */
  const [badgeGroups, setBadgeGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [deleteBadgeId, setDeleteBadgeId] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10;
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");

    const requirementConfig = {
        sessions_count: {
            label: "Sessions Completed",
            color: "info",
        },
        streak_days: {
            label: "Streak Days",
            color: "warning",
        },
        hours_painted: {
            label: "Hours Painted",
            color: "primary",
        },
        projects_completed: {
            label: "Projects Completed",
            color: "success",
        },
        diamonds_placed: {
            label: "Diamonds Placed",
            color: "secondary",
        },
    };
  
    const handleSearch = () => {
        if (searchQuery != "") {
           setCurrentPage(1);
           fetchBadgeDefinitions(1, searchQuery);
        }
    };

    const handleClearSearch = () => {
        if (searchQuery != "") {
            setCurrentPage(1);
            setSearchQuery("");
            fetchBadgeDefinitions(1, "");
        }
    };

    const isAddOrEdit = location.pathname.startsWith("/badges/add") || location.pathname.startsWith("/badges/edit");
    const isEdit = location.pathname.startsWith("/badges/edit");

  const fetchBadgeDefinitions = async (page = 1, search) => {
    try {
      setIsLoading(true);
      let postData = {
        limit,
        page,
        groupByCategory: true,
      };

      if (search && search.trim() !== "") {
        postData.search = search.trim();
      }

      const response = await api.getBadgeDefinitions(postData);
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        setColumns([
          { name: "name", align: "left" },
          { name: "requirement Logic", align: "center" },
          { name: "status", align: "center" },
          { name: "actions", align: "center" },
        ]);

        const mapBadgeToRow = (item) => ({
          linkPath: `/badges`,
          name: (
            <SoftBox display="flex" alignItems="center" px={1} pb={0.5}>
              <SoftBox display="flex" flexDirection="column" ml={2}>
                <SoftTypography variant="button" fontWeight="medium">
                  {item.name}
                </SoftTypography>
              </SoftBox>
            </SoftBox>
          ),
          "requirement Logic": (() => {
            const type = item?.requirement_logic?.type;
            const value = item?.requirement_logic?.threshold;

            const config = requirementConfig[type] || {
              label: type,
              color: "dark",
            };

            return (
              <SoftBadge
                variant="white"
                badgeContent={
                  value !== undefined && value !== null
                    ? `${Number(value).toLocaleString("en-US")} ${
                        config.label?.replace(/completed/i, "").trim()
                      }`
                    : config.label?.replace(/completed/i, "").trim()
                }
                size="xs"
              />
            );
          })(),
          status: (
            <SoftBadge
              variant="gradient"
              badgeContent={
                item.status === "active" ? "Active" : "Inactive"
              }
              color={item.status === "active" ? "success" : "warning"}
              size="xs"
            />
          ),
          actions: (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
            >
              <SoftTypography
                component={Link}
                to={`/badges/edit/?badgeId=${item?._id}`}
                variant="caption"
                color="secondary"
                fontWeight="medium"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  minHeight: "40px",
                }}
              >
                Edit
              </SoftTypography>

              <DeleteTableButton
                onClick={() => {
                  setDeleteBadgeId(item?._id);
                  setIsOpenDeleteDialog(true);
                }}
              />
            </Box>
          ),
        });

        const raw = response.data.badges || [];
        const isGrouped =
          Array.isArray(raw) &&
          raw.length > 0 &&
          raw[0] &&
          typeof raw[0] === "object" &&
          Array.isArray(raw[0].badges);

        if (isGrouped) {
          setBadgeGroups(
            raw.map((group) => ({
              category: group.category,
              rows: (group.badges || []).map(mapBadgeToRow),
            }))
          );
        } else {
          setBadgeGroups([
            { category: null, rows: raw.map(mapBadgeToRow) },
          ]);
        }
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching badge definitions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loginData = JSON.parse(
      localStorage.getItem("loginData") || sessionStorage.getItem("loginData")
    );

    if (!loginData) {
      navigate("/authentication/sign-in");
    } else {
      fetchBadgeDefinitions(currentPage, searchQuery);
    }
  }, [currentPage, navigate, searchQuery]);

  const handlePageClick = (page) => {
    setCurrentPage(page);
    // console.log(page);
    fetchBadgeDefinitions(page, searchQuery);
  };

  const handleClose = () => {
    setIsOpenDeleteDialog(false);
  }

    const handleConfirmDelete = async () => {
        try {
            setIsDeleting(true);

            const data = { 
                badgeId: deleteBadgeId,
            };
            let response = await api.deleteBadgeDefinition(data);
            const apiResponse = new ApiResponse(response.status, response.message);

            if (apiResponse.status === 1) {
                alert(apiResponse.message);
                setIsOpenDeleteDialog(false);
                window.location.href = '/badges';
            } else {
                alert(apiResponse.message);
            }

            } catch (error) {
               console.error("Error deleting badge:", error);
            } finally {
               setIsDeleting(false);
            }
    };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {isAddOrEdit ? (<Outlet /> ) : (
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6" width="20%">
                Badges
              </SoftTypography>
              <SoftBox display="flex" alignItems="center" gap={2}>
                <SearchField
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                />
    
                {/* ➕ Add Button */}
                <SoftButton
                variant="gradient"
                color="info"
                startIcon={<AddIcon />}
                onClick={() => navigate("/badges/add")}
                >
                    Add
                </SoftButton>
            </SoftBox>
              {/* <ProjectFilter status={status} setStatus={setStatus} /> */}
            </SoftBox>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {isLoading ? (
                <SoftBox
                  display="flex"
                  height="500px"
                  justifyContent="center"
                  alignItems="center"
                  p={3}
                >
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    Loading...
                  </SoftTypography>
                </SoftBox>
              ) : badgeGroups.some((g) => g.rows?.length > 0) ? (
                <SoftBox px={2} pb={2}>
                  {badgeGroups
                    .filter((g) => g.rows?.length > 0)
                    .map((group) => {
                      const categoryLabel = group.category
                        ? getCategoryConfig(group.category).label
                        : "Badges";
                      return (
                        <SoftBox key={group.category ?? "all"} mb={4}>
                          <SoftTypography
                            variant="h6"
                            fontWeight="bold"
                            color="dark"
                            mb={2}
                            mt={group.category ? 1 : 0}
                          >
                            {categoryLabel}
                          </SoftTypography>
                          <Table columns={columns} rows={group.rows} />
                        </SoftBox>
                      );
                    })}
                </SoftBox>
              ) : (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    No Badges Data Found
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
            {!badgeGroups.some((g) => g.rows?.length > 0) && (<SoftBox display="flex" alignItems="center" justifyContent="center" py={2}>
              {/* Previous Button */}
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
                sx={{ mx: 1 }}
              >
                Previous
              </SoftButton>

              <SoftButton
                key={currentPage}
                variant="contained"
                color="primary"
                // onClick={() => handlePageClick(pageNumber)}
                sx={{ mx: 0.5 }}
              >
                {currentPage}
              </SoftButton>

              {/* Next Button */}
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === totalPages || totalPages === 0}
                sx={{ mx: 1 }}
              >
                Next
              </SoftButton>
            </SoftBox>)}
          </Card>
        </SoftBox>
      </SoftBox>
      )}
      <ConfirmDialog
        isOpen={isOpenDeleteDialog}
        handleClose={handleClose}
        handleConfirm={handleConfirmDelete}
        isLoading={isDeleting}
        title={
            "Are you sure you want to delete this badge completely?"
        }
        actionText={"Yes, Delete"}
      />
      <Footer />
    </DashboardLayout>
  );
}
